import { adminRpc } from "../../../services/admin-rpc-client";

// Can the host behind this worker actually run a daemon job?
//
// A daemon job is launched through the privileged executor, which only some job-runners
// have. A daemon assigned to a runner without one is accepted, stored, and then never
// starts — silence being exactly the failure mode the form exists to prevent
// (FEATURE-20260720-daemon-capable-job-runner-tracking, which put the answer on the
// wire: every runner publishes daemon_capable on its ProcessStart, surfaced by the
// firewall as processrun.daemoncapable).
//
// Read from the runner's LIVE processrun, because capability is a property of how the
// runner was started, not of the host in the abstract: the same box can carry a capable
// runner for one environment and an incapable one for another.

const esc = (s: string) => s.replace(/'/g, "''");

/** What is known about a worker; undefined = not looked up yet, not "incapable". */
const cache = new Map<string, boolean>();

/** The cached answer, for the synchronous form validator. undefined = unknown, and an
 *  unknown answer never blocks a submit — the runner is the authority, and a lookup
 *  that has not landed is not evidence of anything. */
export function knownDaemonCapability(workerUid: string | undefined): boolean | undefined {
  if (!workerUid) return undefined;
  return cache.get(workerUid);
}

/** Ask whether this worker's runner is daemon-capable, and remember the answer.
 *  A failed lookup resolves undefined and is not cached, so it is retried rather than
 *  hardening into a wrong "no". */
export async function fetchDaemonCapability(workerUid: string): Promise<boolean | undefined> {
  if (!workerUid) return undefined;
  const cached = cache.get(workerUid);
  if (cached !== undefined) return cached;
  try {
    const resp = await adminRpc.query(
      `SELECT daemoncapable FROM processrun WHERE workeruid = '${esc(workerUid)}' ` +
        `AND completedat IS NULL AND daemoncapable IS NOT NULL ORDER BY startedat DESC LIMIT 1`,
    );
    const row = resp.rows[0];
    if (!row) return undefined; // no live runner on that worker — nothing to conclude
    const capable = String(row["daemoncapable"] ?? "").toLowerCase() === "true";
    cache.set(workerUid, capable);
    return capable;
  } catch {
    return undefined;
  }
}

/** Test seam: the cache is module state, and a test that asserts the unknown path must
 *  be able to get back to it. */
export function resetDaemonCapabilityCache(): void {
  cache.clear();
}
